import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InspoJourney } from '../../services';
import { useEmbed } from '../book/useEmbed';
import { InspoContent } from './Inspo.component';
import { InspoCtaProvider } from './components/InspoCta';

/** The real Wallmates logo (same files the public pricing page uses). */
const logo = (mode: 'light' | 'dark'): string =>
    `https://${process.env.REACT_APP_AWS_BUCKET_IMAGES || ''}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Logo-${mode}-mode.png`;

// #region PublicInspo
/**
 * Public, no-login brand stories. On its own it is a full page; with ?embed=1 inside an iframe
 * (see public/embed/inspo.js) it is just the stories, and the host page decides what
 * "Get started" opens. ?path=store opens on the store journey (artist is the default).
 */
const PublicInspo: React.FunctionComponent = (): React.ReactElement => {
    const [params] = useSearchParams();
    const embedded = useEmbed(params);
    const navigate = useNavigate();
    const initialJourney: InspoJourney = params.get('path') === 'store' ? 'store' : 'artist';

    React.useEffect(() => { document.title = 'Brand stories · Wallmates'; }, []);

    const getStarted = (journey: InspoJourney) => {
        if (embedded) {
            window.parent.postMessage({ type: 'wallmates:inspo_cta', path: journey }, '*');
            return;
        }
        navigate(`/get-started?src=${encodeURIComponent(params.get('src') || `stories_page_${journey}`)}`);
    };

    const content = (
        <InspoCtaProvider publicMode getStarted={getStarted}>
            <InspoContent publicMode initialJourney={initialJourney} />
        </InspoCtaProvider>
    );

    if (embedded) return <div data-embed-root className="p-px">{content}</div>;

    return (
        <div className="h-screen overflow-y-auto bg-surface-sunken px-4 py-6 sm:py-10">
            <div className="mx-auto w-full max-w-7xl">
                <a href="https://wallmates.com" className="mb-6 inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">
                    <img className="block h-[29px] w-[129px] dark:hidden" src={logo('light')} alt="Wallmates" />
                    <img className="hidden h-[29px] w-[129px] dark:block" src={logo('dark')} alt="Wallmates" />
                </a>
                {content}
            </div>
        </div>
    );
};

export default PublicInspo;
